import type * as React from 'react';
import { createFixedColumnStyle } from '@utils';
import { ACTION_COLUMN_WIDTH, SELECTION_COLUMN_WIDTH } from '../../data-grid.constants';

interface IResolveCellWidthParams {
	isFirstColumn: boolean;
	isActionsColumn: boolean;
	hasRowSelection: boolean;
	hasExplicitWidth: boolean;
	size?: number;
}

interface IResolveHeaderWidthParams {
	isSelectionColumn: boolean;
	fallbackWidth: number | string;
}

/**
 * Resolves cell sizing for the data-grid body and loading skeleton.
 * The selection column is always pinned (width/min/max) so the checkbox can never be clipped.
 * Columns with an explicit width pin to their configured size (this takes priority over the
 * default action width, so inline action columns keep their wider size). Action columns without
 * an explicit width fall back to the default action width. All other columns are auto width.
 */
export function resolveDataGridCellWidth({
	isFirstColumn,
	isActionsColumn,
	hasRowSelection,
	hasExplicitWidth,
	size,
}: IResolveCellWidthParams): React.CSSProperties | undefined {
	if (isFirstColumn && hasRowSelection) {
		return createFixedColumnStyle(SELECTION_COLUMN_WIDTH);
	}

	if (hasExplicitWidth && size) {
		return createFixedColumnStyle(`${size}px`);
	}

	if (isActionsColumn) {
		return createFixedColumnStyle(ACTION_COLUMN_WIDTH);
	}

	return undefined;
}

/**
 * Extracts the pinned maxWidth from a resolved cell style, shared by the real body rows and the
 * loading skeleton so both pass the same clamp to TablePrimitiveCell.
 */
export function resolveDataGridCellMaxWidth(style: React.CSSProperties | undefined): string | undefined {
	return typeof style?.maxWidth === 'string' ? style.maxWidth : undefined;
}

/**
 * Resolves header sizing. Only the selection header is pinned; all other headers (including
 * actions, which relies on w-auto/ml-auto classes to hug the right edge) keep their table size.
 */
export function resolveDataGridHeaderWidth({
	isSelectionColumn,
	fallbackWidth,
}: IResolveHeaderWidthParams): React.CSSProperties {
	if (isSelectionColumn) {
		return createFixedColumnStyle(SELECTION_COLUMN_WIDTH);
	}

	return { width: fallbackWidth };
}
